import { Button, showToast } from '@ideoz/ui';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStartupPageFacade } from '@ideoz/core';
import { CreateScopeDialog } from './components/CreateScopeDialog';

interface NewScopeActionProps {
  startupId: string;
}

/**
 * Self-contained "New Scope" button + create dialog for startup-level pages that
 * don't render the scopes list (Home, Artifacts, …). It owns its own facade
 * (`autoFetch: false`, no list) and lets the dialog auto-evaluate the new scope
 * via the raw fallback — there's no ICE badge on these pages to keep in sync.
 *
 * The Work Scopes page does *not* use this; there the create action shares the
 * list's facade + ICE so the new card shows "Scoring…".
 */
export function NewScopeAction({ startupId }: NewScopeActionProps): JSX.Element {
  const { t } = useTranslation();
  const facade = useStartupPageFacade({
    navigateToScope: () => {},
    startupId,
    autoFetch: false,
  });

  const handleCreateScope = async (data: {
    title: string;
    description?: string;
    newFiles?: File[];
  }): Promise<string | void> => {
    try {
      const scopeId = await facade.createScope(data);
      showToast({ message: t('workScopes.toast.created'), severity: 'success' });
      return scopeId;
    } catch (err) {
      showToast({ message: t('workScopes.toast.createFailed'), severity: 'error' });
      throw err;
    }
  };

  return (
    <>
      <Button variant="primary" size="xs" onClick={facade.openCreateDialog} className="gap-1.5">
        <Plus size={14} />
        {t('workScopes.newScope')}
      </Button>

      <CreateScopeDialog
        open={facade.createDialogOpen}
        onClose={facade.closeDialog}
        onSubmit={handleCreateScope}
        startupId={startupId}
        isLoading={facade.isCreatingScope || facade.isMutatingScope}
        mode="create"
      />
    </>
  );
}

export default NewScopeAction;
